import apiClient from '@/api/client';
import type { PaginatedResponse } from '@/types/pagination';

export interface Company {
  id: string;
  cnpj: string;
  legalName: string;
  adminPhone: string | null;
  managedCompanies: string | null;
  accountMode: 'standard' | 'trial';
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  trialLifecycleStatus: 'active' | 'expired' | 'cleaned';
  trialExpiredAt: string | null;
  trialCleanedAt: string | null;
  commercialContactRequestedAt: string | null;
  trialUploadLimit: number;
  trialUploadsUsed: number;
  customerLimit: number;
  aiTokenLimit: number;
  storageLimit: number;
  aiTokensUsed: number;
  storageUsed: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyUsage {
  id: string;
  cnpj: string;
  legalName: string;
  adminPhone: string | null;
  managedCompanies: string | null;
  accountMode: 'standard' | 'trial';
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  trialLifecycleStatus: 'active' | 'expired' | 'cleaned';
  trialExpiredAt: string | null;
  trialCleanedAt: string | null;
  commercialContactRequestedAt: string | null;
  trialUploadLimit: number;
  trialUploadsUsed: number;
  deletedAt: string | null;
  customerLimit: number;
  customerCount: number;
  customerPercentage: number;
  aiTokenLimit: number;
  aiTokensUsed: number;
  aiPercentage: number;
  storageLimit: number;
  storageUsed: number;
  storagePercentage: number;
}

export interface CreateCompanyInput {
  cnpj: string;
  legalName: string;
  customerLimit?: number;
  aiTokenLimit?: number;
  storageLimit?: number;
}

export interface UpdateCompanyInput {
  legalName?: string;
  customerLimit?: number;
  aiTokenLimit?: number;
  storageLimit?: number;
}

export interface CnpjConflictPayload {
  error: string;
  companyId?: string;
  deletedAt?: string;
  legalName?: string;
}

export interface ListCompaniesUsageParams {
  page?: number;
  limit?: number;
  includeDeleted?: boolean;
}

export interface CompanyUserRow {
  id: string;
  email: string;
  fullName: string;
  role: 'admin' | 'operation';
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCompanyUserInput {
  email: string;
  password: string;
  fullName: string;
  role: 'admin' | 'operation';
  companyId: string;
}

export interface UpdateCompanyUserInput {
  password?: string;
  fullName?: string;
  role?: 'admin' | 'operation';
  isActive?: boolean;
}

export interface CompanyCustomerRow {
  id: string;
  cnpj: string;
  legalName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CleanupExpiredTrialsResponse {
  cleanedCompanyIds: string[];
}

export const companiesApi = {
  async listUsage(params: ListCompaniesUsageParams = {}): Promise<PaginatedResponse<CompanyUsage>> {
    const search = new URLSearchParams();
    if (params.page) search.set('page', String(params.page));
    if (params.limit) search.set('limit', String(params.limit));
    if (params.includeDeleted !== undefined) {
      search.set('includeDeleted', params.includeDeleted ? 'true' : 'false');
    }
    const qs = search.toString();
    const response = await apiClient.get<PaginatedResponse<CompanyUsage>>(
      `/admin/companies/usage${qs ? `?${qs}` : ''}`
    );
    return response.data;
  },

  async listActiveTrials(params: ListCompaniesUsageParams = {}): Promise<PaginatedResponse<CompanyUsage>> {
    const search = new URLSearchParams();
    if (params.page) search.set('page', String(params.page));
    if (params.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    const response = await apiClient.get<PaginatedResponse<CompanyUsage>>(
      `/admin/trial-companies/active${qs ? `?${qs}` : ''}`
    );
    return response.data;
  },

  async listTrials(params: ListCompaniesUsageParams = {}): Promise<PaginatedResponse<CompanyUsage>> {
    const search = new URLSearchParams();
    if (params.page) search.set('page', String(params.page));
    if (params.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    const response = await apiClient.get<PaginatedResponse<CompanyUsage>>(
      `/admin/trial-companies${qs ? `?${qs}` : ''}`
    );
    return response.data;
  },

  async cleanupExpiredTrials(): Promise<CleanupExpiredTrialsResponse> {
    const response = await apiClient.post<CleanupExpiredTrialsResponse>(
      '/admin/trial-companies/cleanup-expired'
    );
    return response.data;
  },

  async getUsage(companyId: string): Promise<CompanyUsage> {
    const response = await apiClient.get<CompanyUsage>(`/admin/companies/${companyId}/usage`);
    return response.data;
  },

  async getById(companyId: string): Promise<Company> {
    const response = await apiClient.get<Company>(`/companies/${companyId}`);
    return response.data;
  },

  async create(input: CreateCompanyInput): Promise<Company> {
    const response = await apiClient.post<Company>('/companies', input);
    return response.data;
  },

  async update(companyId: string, input: UpdateCompanyInput): Promise<Company> {
    const response = await apiClient.put<Company>(`/companies/${companyId}`, input);
    return response.data;
  },

  async deactivate(companyId: string): Promise<Company> {
    const response = await apiClient.post<Company>(`/companies/${companyId}/deactivate`);
    return response.data;
  },

  async activate(companyId: string): Promise<Company> {
    const response = await apiClient.post<Company>(`/companies/${companyId}/activate`);
    return response.data;
  },

  async listUsers(companyId: string): Promise<CompanyUserRow[]> {
    const response = await apiClient.get<CompanyUserRow[]>(`/companies/${companyId}/users`);
    return response.data;
  },

  async createUser(input: CreateCompanyUserInput): Promise<CompanyUserRow> {
    const response = await apiClient.post<CompanyUserRow>('/user-companies', input);
    return response.data;
  },

  async updateUser(userId: string, input: UpdateCompanyUserInput): Promise<CompanyUserRow> {
    const response = await apiClient.put<CompanyUserRow>(`/user-companies/${userId}`, input);
    return response.data;
  },

  async deleteUser(userId: string): Promise<void> {
    await apiClient.delete(`/user-companies/${userId}`);
  },

  async activateUser(userId: string): Promise<CompanyUserRow> {
    const response = await apiClient.post<CompanyUserRow>(`/user-companies/${userId}/activate`);
    return response.data;
  },

  async deactivateUser(userId: string): Promise<CompanyUserRow> {
    const response = await apiClient.post<CompanyUserRow>(`/user-companies/${userId}/deactivate`);
    return response.data;
  },

  async listCustomers(companyId: string): Promise<CompanyCustomerRow[]> {
    const response = await apiClient.get<CompanyCustomerRow[]>(`/companies/${companyId}/customers`);
    return response.data;
  },

  async impersonate(companyId: string): Promise<{ code: string; clientAppUrl: string }> {
    const response = await apiClient.post<{ code: string; clientAppUrl: string }>(
      `/auth/admin/impersonate/${companyId}`
    );
    return response.data;
  },
};
